<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import CopyButton from '@/components/CopyButton.vue'
import GbButton from '@/components/GbButton.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import SaveToLibraryPanel from '@/presentlevels/no-ai/SaveToLibraryPanel.vue'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useLibraryStore } from '@/library/library.store'
import { populatePlaceholderText } from '@/presentlevels/api_v1.js'
import { SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW } from '@/contentprofile/no_sel_composable'
import { CONCLUSION_EVENT_NAMES } from '@/presentlevels/no-ai/event_names'
import { RESULTS_ROUTE_NAME } from '@/presentlevels/no-ai/conclusion_route_names'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import { announce } from '@/util/live_announcer'
import { betaOptIn } from '@/util/beta_opt_in'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'

const GENERATING_MESSAGE = 'Generating statement...'
const READY_MESSAGE = 'Present levels statement ready'
const COPIED_MESSAGE = 'Present levels statement copied'
const READ_ONLY_MESSAGE = 'The statement below cannot be edited, since it was created using a previous version of the Present Levels Wizard.'

const props = defineProps<{
  uuid: string
}>()

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const wizardStore = usePresentLevelsWizardStore()
const { conclusionStatement, studentName, enrolledGrade, lastUpdate, isSaving, developPermissions, blurPermissions } =
  storeToRefs(wizardStore)
const { selVisibility, effectsVisibility, aiFeatureVisibility } = storeToRefs(useVisibilityStore())
const { currentFolderUuid, canEditCurrentFolderContent } = storeToRefs(useLibraryStore())

const isReadOnlyTooltipShown = ref(false)

// Statements from the first wizard version are read-only for AI-profile users, who reach them from their library.
const isReadOnlyStatement = computed(() => effectsVisibility.value === false && aiFeatureVisibility.value === true)
const isGenerating = computed(() => conclusionStatement.value === null)

const generatingStatusAttrs = computed(() => {
  if (!isGenerating.value) {
    return {}
  }
  return { role: 'status', 'aria-live': 'polite' as const }
})

function withSelAreaLabel(text: string) {
  if (selVisibility.value) {
    return text
  }
  return text
    .replaceAll(SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW)
    .replaceAll(SEL_DEFAULT_AREA_PLW.toUpperCase(), SEL_REPLACEMENT_STRATEGIES_PLW.toUpperCase())
}

const displayedStatement = computed(() => {
  const statement = conclusionStatement.value
  if (statement === null) {
    return GENERATING_MESSAGE
  }
  const plainStatement = withSelAreaLabel(wikiToPlainText(statement))
  return populatePlaceholderText(plainStatement, { studentName: studentName.value, studentGrade: enrolledGrade.value })
})

// AI-profile users may read a first-version statement but are never shown it blurred.
const isStatementBlurred = computed(() => {
  if (aiFeatureVisibility.value === true) {
    return false
  }
  return blurPermissions.value.blur
})

const isSavedInUneditableFolder = computed(() => Boolean(currentFolderUuid.value) && !canEditCurrentFolderContent.value)
const isEditButtonShown = computed(() => !isReadOnlyStatement.value && !isSavedInUneditableFolder.value)

const isDevelopingBlocked = computed(() => developPermissions.value.block)

const lastSavedAt = computed(() => lastUpdate.value?.toString())

// The panel looks the folder up again before it reports a removal, so the folder the statement
// was removed from has to be remembered while it is still the current one.
const statementFolderUuid = ref('')
watch(currentFolderUuid, (folderUuid) => {
  if (folderUuid) {
    statementFolderUuid.value = folderUuid
  }
}, { immediate: true })

function announceStatementProgress(statement: string, previousStatement: string | undefined) {
  const hasStartedGenerating = statement === GENERATING_MESSAGE && previousStatement !== statement
  if (hasStartedGenerating) {
    announce(GENERATING_MESSAGE)
    return
  }
  const isReady = previousStatement === GENERATING_MESSAGE && statement.trim().length > 0
  if (isReady) {
    announce(READY_MESSAGE)
  }
}

watch(displayedStatement, announceStatementProgress, { immediate: true })

function sendCopiedEvent() {
  analytics.sendEvent(CONCLUSION_EVENT_NAMES.CLICKED_COPY_STATEMENT)
}

function statementCopied() {
  sendCopiedEvent()
  announce(COPIED_MESSAGE)
}

function blockCopying() {
  sendCopiedEvent()
  wizardStore.blockIfDevelopingIsNotAllowed()
}

function cameFromResults() {
  const previousPath = window.history.state?.back
  if (typeof previousPath !== 'string') {
    return false
  }
  return router.resolve(previousPath).name === RESULTS_ROUTE_NAME
}

function editStatements() {
  analytics.sendEvent(CONCLUSION_EVENT_NAMES.CLICKED_EDIT_STATEMENTS)
  if (wizardStore.blockIfDevelopingIsNotAllowed()) {
    return
  }
  if (cameFromResults()) {
    router.back()
    return
  }
  router.replace({ name: RESULTS_ROUTE_NAME, params: { uuid: props.uuid }, query: betaOptIn(route.query) })
}

function showReadOnlyTooltip() {
  if (isReadOnlyStatement.value) {
    isReadOnlyTooltipShown.value = true
  }
}

function hideReadOnlyTooltip() {
  isReadOnlyTooltipShown.value = false
}

// A read-only statement can't be saved again, so once removed there is nothing left to do here.
function leaveRemovedReadOnlyStatement() {
  if (!isReadOnlyStatement.value) {
    return
  }
  router.push({ name: 'LibraryFolder', params: { uuid: statementFolderUuid.value } })
}
</script>

<template>
  <div>
    <div
      v-if="isReadOnlyStatement"
      class="mb-4 flex items-start rounded-xs border border-blue-300 bg-blue-200 p-4 text-blue-500"
      data-testid="read-only-banner"
    >
      <span class="material-icons-round mr-4 mt-0.5 shrink-0 text-text-2xl" aria-hidden="true">lock</span>
      <div class="flex-1">
        <strong class="mb-2 block text-lg">{{ READ_ONLY_MESSAGE }}</strong>
        <p class="m-0 text-md leading-normal">You may still copy the statement text, as well as view aligned assessments and aligned goals associated with the statement.</p>
      </div>
    </div>

    <div class="flex flex-col gap-4 min-[992px]:flex-row">
      <div class="min-[992px]:w-9/12 xl:w-8/12">
        <div class="mt-2.5 flex flex-wrap justify-start gap-1">
          <GbButton
            v-if="isDevelopingBlocked"
            type="button"
            gb-theme="grayOutlineSecondary"
            @click="blockCopying"
          >Copy Statement</GbButton>
          <CopyButton
            v-else
            :string-to-copy="displayedStatement"
            button-text="Copy Statement"
            button-theme="grayOutlineSecondary"
            :tooltip-text="COPIED_MESSAGE"
            auto-hide-tooltip
            @copied="statementCopied"
          >
            <template #icon><span /></template>
          </CopyButton>
          <GbButton
            v-if="isEditButtonShown"
            type="button"
            gb-theme="bluePrimary"
            @click="editStatements"
          >Edit Statements</GbButton>
        </div>

        <div
          class="relative mt-4 border border-gray-400 p-4"
          :class="{ 'bg-gray-200': isReadOnlyStatement }"
          data-testid="statement-card"
        >
          <!-- The hover tooltip repeats the read-only banner above, so it is pointer-only and hidden from assistive tech. -->
          <div
            v-if="isReadOnlyTooltipShown"
            aria-hidden="true"
            class="absolute inset-x-0 top-0 z-10 flex items-center bg-black px-4 py-3 text-md text-white"
            data-testid="read-only-tooltip"
          >
            <span class="material-icons-round mr-2" aria-hidden="true">lock</span> {{ READ_ONLY_MESSAGE }}
          </div>
          <section
            class="p-4"
            :class="{ 'cursor-not-allowed [&_*]:cursor-not-allowed': isReadOnlyStatement }"
            v-bind="generatingStatusAttrs"
            @mouseenter="showReadOnlyTooltip"
            @mouseleave="hideReadOnlyTooltip"
          >
            <WikiText
              class="fs-exclude p-2 text-text-2xl font-[300] leading-8"
              :class="{ 'bg-white': !isReadOnlyStatement }"
              :text="displayedStatement"
              :locked="isStatementBlurred"
            />
          </section>
        </div>
      </div>

      <div class="min-[992px]:w-3/12 xl:w-4/12">
        <SaveToLibraryPanel
          :content-uuid="uuid"
          :last-saved-at="lastSavedAt"
          :is-content-saving="isSaving"
          :is-save-button-hidden="isReadOnlyStatement"
          @removed="leaveRemovedReadOnlyStatement"
        />
      </div>
    </div>
  </div>
</template>
